import { usePathname } from 'next/navigation';
import { createContext, useEffect, useState } from 'react';

export interface Or2HistoryPage {
  pathname: string;
  topLevelRoute: string;
  identifier: string | null;
}

interface Or2HistoryContextValue {
  lastPage: Or2HistoryPage | null;
}

export const Or2HistoryContext = createContext<Or2HistoryContextValue>({
  lastPage: null,
});

const getPageInfoFromPathname = (pathname: string): Or2HistoryPage => ({
  pathname,
  topLevelRoute: pathname.split('/')[1] ?? null,
  identifier: pathname.split('/')[2] ?? null,
});

interface Or2HistoryContextProviderProps {
  children: React.ReactNode;
}

export const Or2HistoryContextProvider = ({
  children,
}: Or2HistoryContextProviderProps) => {
  const [cachedCurrentPage, setCachedCurrentPage] =
    useState<Or2HistoryPage | null>(null);
  const [cachedPreviousPage, setCachedPreviousPage] =
    useState<Or2HistoryPage | null>(null);

  const pathname = usePathname();
  const newPage = getPageInfoFromPathname(pathname);

  // Only replace the cached pages if we have moved to new object (user, group, service, etc) or if there is no identifier
  // Always keep the current page up to date
  useEffect(() => {
    if (
      newPage?.identifier !== cachedCurrentPage?.identifier ||
      cachedCurrentPage?.identifier === null
    ) {
      setCachedPreviousPage(cachedCurrentPage);
    }
    if (newPage.pathname !== cachedCurrentPage?.pathname) {
      setCachedCurrentPage(newPage);
    }
  }, [pathname, newPage, cachedCurrentPage]);

  return (
    <Or2HistoryContext.Provider
      value={{
        lastPage: cachedPreviousPage,
      }}
    >
      {children}
    </Or2HistoryContext.Provider>
  );
};
